import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";

// Server-backed Turkish speech-to-text — MediaRecorder + POST /api/voice/transcribe.
//
// This hook is ONLY exercised when the browser lacks a usable
// SpeechRecognition surface (Firefox, older iOS Safari). The native
// path in `useVoiceInput` remains primary and is checked first by the
// Chat page. The public API here mirrors `useVoiceInput` so the UI
// (VoicePreview, MicButton) does not need to branch.
//
//   * Single utterance per tap. MediaRecorder is stopped on manual
//     tap OR on the hard duration cap (30s). No continuous listening.
//   * Audio blob held in memory only. It is uploaded and then dropped.
//     The server side is responsible for not persisting the payload.
//   * MIME is inferred via ``MediaRecorder.isTypeSupported`` — we do
//     NOT hard-code a list that breaks iOS Safari. On failure to find
//     any supported type, we report ``supported=false`` and the caller
//     renders the same disabled MicButton state used for native
//     unsupported browsers.
//   * Governance: the hook exposes no vendor / model / engine
//     identifier. All error strings surfaced to the UI are Turkish.

const MAX_DURATION_MS = 30000;

// Prefer widely-supported codecs first. iOS Safari commonly returns
// ``audio/mp4`` from `isTypeSupported`; Chrome/Firefox return
// ``audio/webm``. Ogg is a fallback.
const CANDIDATE_MIMES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
  "audio/ogg",
];

function pickSupportedMime() {
  if (typeof window === "undefined" || !window.MediaRecorder) return null;
  for (const m of CANDIDATE_MIMES) {
    try {
      if (window.MediaRecorder.isTypeSupported(m)) return m;
    } catch (_) { /* older iOS may throw */ }
  }
  // Some Safari builds return true for empty type — pick a safe default.
  try {
    if (window.MediaRecorder.isTypeSupported("")) return "audio/mp4";
  } catch (_) { /* noop */ }
  return null;
}

export function useServerVoiceInput() {
  const canGetMic =
    typeof navigator !== "undefined" &&
    typeof navigator.mediaDevices?.getUserMedia === "function";
  const supportedMime = typeof window !== "undefined" ? pickSupportedMime() : null;
  const supported = Boolean(canGetMic && supportedMime && typeof window !== "undefined" && window.MediaRecorder);

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const startTsRef = useRef(0);
  const hardStopTimerRef = useRef(null);

  // Status: "idle" | "listening" | "uploading" | "denied" | "error" | "unsupported"
  const [status, setStatus] = useState(supported ? "idle" : "unsupported");
  const [interim, setInterim] = useState("");
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState(null);

  const releaseStream = useCallback(() => {
    try {
      streamRef.current?.getTracks?.().forEach((t) => t.stop());
    } catch (_) { /* noop */ }
    streamRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      try { recorderRef.current?.stop(); } catch (_) { /* noop */ }
      if (hardStopTimerRef.current) {
        clearTimeout(hardStopTimerRef.current);
        hardStopTimerRef.current = null;
      }
      releaseStream();
    };
  }, [releaseStream]);

  const uploadAndTranscribe = useCallback(async (blob, mime) => {
    setStatus("uploading");
    setInterim("");
    const durationMs = Math.max(0, Math.min(MAX_DURATION_MS, Date.now() - startTsRef.current));
    try {
      const form = new FormData();
      form.append("audio", blob, `utterance.${mime.includes("mp4") ? "mp4" : mime.includes("ogg") ? "ogg" : "webm"}`);
      form.append("duration_ms", String(durationMs));
      const r = await api.post("/voice/transcribe", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const text = (r.data?.text || "").trim();
      if (!text) {
        setStatus("idle");
        setError("Konuşmadan metin çıkarılamadı, lütfen tekrar dene.");
        return;
      }
      setTranscript(text);
      setStatus("idle");
    } catch (e) {
      const httpStatus = e?.response?.status;
      let msg = formatApiError(e) || "Sesli girişi metne çeviremedik, lütfen tekrar dene.";
      if (httpStatus === 429) msg = "Günlük sesli giriş sınırına ulaştın.";
      else if (httpStatus === 413) msg = "Ses dosyası çok büyük.";
      else if (httpStatus === 415) msg = "Bu ses biçimi desteklenmiyor.";
      else if (httpStatus === 403) msg = "Demo hesabında sesli giriş kullanılamıyor.";
      else if (httpStatus === 503) msg = "Sesli giriş şu an kullanılamıyor. Lütfen daha sonra dene.";
      else if (httpStatus && httpStatus >= 500) msg = "Sesli giriş şu an yanıt vermiyor. Lütfen tekrar dene.";
      setStatus("error");
      setError(msg);
    }
  }, []);

  const start = useCallback(async () => {
    if (!supported) {
      setStatus("unsupported");
      return;
    }
    setError(null);
    setInterim("");
    setTranscript("");
    chunksRef.current = [];

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      const name = e?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        setStatus("denied");
        setError("Mikrofon izni verilmedi");
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        setStatus("error");
        setError("Mikrofon bulunamadı");
      } else {
        setStatus("error");
        setError("Mikrofon açılamadı");
      }
      return;
    }
    streamRef.current = stream;

    let recorder;
    try {
      recorder = new window.MediaRecorder(stream, { mimeType: supportedMime });
    } catch (_) {
      // Some browsers accept the constructor without an explicit mimeType.
      try {
        recorder = new window.MediaRecorder(stream);
      } catch (e2) {
        releaseStream();
        setStatus("error");
        setError("Ses kaydı başlatılamadı");
        return;
      }
    }
    recorderRef.current = recorder;
    startTsRef.current = Date.now();

    recorder.ondataavailable = (evt) => {
      if (evt?.data && evt.data.size > 0) chunksRef.current.push(evt.data);
    };
    recorder.onerror = () => {
      setStatus("error");
      setError("Ses kaydı sırasında bir sorun oluştu");
      releaseStream();
    };
    recorder.onstop = () => {
      if (hardStopTimerRef.current) {
        clearTimeout(hardStopTimerRef.current);
        hardStopTimerRef.current = null;
      }
      const mime = recorder.mimeType || supportedMime || "audio/webm";
      const blob = new Blob(chunksRef.current, { type: mime });
      chunksRef.current = [];
      releaseStream();
      if (!blob || blob.size === 0) {
        setStatus("idle");
        setError("Ses algılanmadı, tekrar dene");
        return;
      }
      // Fire-and-forget upload; caller renders "uploading" state.
      uploadAndTranscribe(blob, mime);
    };

    try {
      recorder.start();
      setStatus("listening");
      // Hard client-side cap. Server also enforces its own cap.
      hardStopTimerRef.current = setTimeout(() => {
        try { recorder.stop(); } catch (_) { /* noop */ }
      }, MAX_DURATION_MS);
    } catch (_) {
      releaseStream();
      setStatus("error");
      setError("Ses kaydı başlatılamadı");
    }
  }, [releaseStream, supported, supportedMime, uploadAndTranscribe]);

  const stop = useCallback(() => {
    try { recorderRef.current?.stop(); } catch (_) { /* noop */ }
  }, []);

  const reset = useCallback(() => {
    setInterim("");
    setTranscript("");
    setError(null);
    if (status !== "listening" && status !== "uploading") {
      setStatus(supported ? "idle" : "unsupported");
    }
  }, [status, supported]);

  return {
    supported,
    status,
    interim,
    transcript,
    error,
    start,
    stop,
    reset,
  };
}
